import { useI18n } from 'vue-i18n'
import { toast } from 'vue-sonner'
import { jsonapiClient } from '@/api/client'
import type { useOrganizationChannel } from '@/composables/useOrganizationChannel'

/**
 * Registers a handler on the org channel for Import-type updates.
 * When the channel broadcasts an import completion, fetches the latest
 * status and shows a toast. Intended to be mounted once in ConsoleApp.vue.
 *
 * Returns an unsubscribe function for cleanup.
 */
export function useImportNotifications(
  channel: ReturnType<typeof useOrganizationChannel>
): () => void {
  const { t } = useI18n()

  const unsubscribe = channel.onMessage(async ({ updates }) => {
    const importUpdates = updates.filter(u => u.type === 'Import')
    for (const { id } of importUpdates) {
      try {
        const response = await jsonapiClient.get('/api/imports/{id}', {
          path: { id },
        })
        const imp = response.data.data
        const { status, resource_type, created_count } = imp.attributes

        if (status === 'done') {
          toast.success(t('imports.notifications.done', { type: resource_type, count: created_count }))
        } else if (status === 'failed') {
          toast.error(t('imports.notifications.failed', { type: resource_type }))
        }
      } catch {
        // swallow — if we can't fetch the import, no toast is better than a crash
      }
    }
  })

  return unsubscribe
}
